import { apiRequest } from "./backendApi";

export type SchedulerTriggeredBy = "cron" | "manual" | "dry_run";

export type SchedulerEntryOutcome =
  | "enqueued"
  | "ready"
  | "duplicate_run"
  | "agent_not_found"
  | "agent_disabled"
  | "dataset_not_found"
  | "connector_unreachable"
  | "error";

export type SchedulerReportEntry = {
  ownerUid: string;
  agentId: string;
  datasetId: string;
  datasetName?: string | null;
  outcome: SchedulerEntryOutcome;
  runId?: string;
  reason?: string;
};

export type SchedulerReport = {
  reportId: string;
  triggeredBy: SchedulerTriggeredBy;
  startedAt: string;
  completedAt: string | null;
  totalPairs: number;
  enqueued: number;
  skipped: number;
  failed: number;
  entries: SchedulerReportEntry[];
};

export type ListSchedulerReportsResult = {
  reports: SchedulerReport[];
  count: number;
  total: number;
};

export type SchedulerConfig = {
  cron: string;
  timezone: string;
  nextExecutionAt: string | null;
  restartRequiredToApplyEnvChanges: boolean;
};

type SchedulerReportApi = {
  report_id: string;
  triggered_by: SchedulerTriggeredBy;
  started_at: string;
  completed_at: string | null;
  total_pairs: number;
  enqueued: number;
  skipped: number;
  failed: number;
  entries: Array<{
    owner_uid: string;
    agent_id: string;
    dataset_id: string;
    dataset_name?: string | null;
    outcome: SchedulerEntryOutcome;
    run_id?: string;
    reason?: string;
  }>;
};

type SchedulerReportsListResponse = {
  data: SchedulerReportApi[];
  count: number;
  total: number;
};

const toReportEntry = (row: SchedulerReportApi["entries"][number]): SchedulerReportEntry => ({
  ownerUid: row.owner_uid,
  agentId: row.agent_id,
  datasetId: row.dataset_id,
  datasetName: row.dataset_name,
  outcome: row.outcome,
  runId: row.run_id,
  reason: row.reason,
});

const toReport = (row: SchedulerReportApi): SchedulerReport => ({
  reportId: row.report_id,
  triggeredBy: row.triggered_by,
  startedAt: row.started_at,
  completedAt: row.completed_at,
  totalPairs: row.total_pairs,
  enqueued: row.enqueued,
  skipped: row.skipped,
  failed: row.failed,
  entries: row.entries.map(toReportEntry),
});

export const schedulerApi = {
  async getConfig(): Promise<SchedulerConfig> {
    const row = await apiRequest<{
      cron: string;
      timezone: string;
      next_execution_at: string | null;
      restart_required_to_apply_env_changes: boolean;
    }>("GET", "/api/scheduler/config");
    return {
      cron: row.cron,
      timezone: row.timezone,
      nextExecutionAt: row.next_execution_at,
      restartRequiredToApplyEnvChanges: row.restart_required_to_apply_env_changes,
    };
  },

  async listReports(options?: { page?: number; limit?: number }): Promise<ListSchedulerReportsResult> {
    const res = await apiRequest<SchedulerReportsListResponse>("GET", "/api/scheduler/reports", {
      query: {
        page: options?.page ?? 1,
        limit: options?.limit ?? 20,
      },
    });
    return {
      reports: res.data.map(toReport),
      count: res.count,
      total: res.total,
    };
  },

  async dryRun(): Promise<SchedulerReport> {
    const row = await apiRequest<SchedulerReportApi>("POST", "/api/scheduler/dry-run");
    return toReport(row);
  },

  async triggerPass(): Promise<SchedulerReport> {
    const row = await apiRequest<SchedulerReportApi>("POST", "/api/scheduler/trigger");
    return toReport(row);
  },
};
